import { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import {
  PhoneXMarkIcon,
  ClockIcon,
  UserCircleIcon,
  MicrophoneIcon,
  SpeakerWaveIcon,
  InboxArrowDownIcon,
  HashtagIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { useTwilio } from "../../context/TwilioContext";
import callService from "../../services/callService";

const DTMF_KEYS = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["*", "0", "#"],
];

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const ActiveCallWidget = ({
  lead,
  onEndCall,
  onCallComplete,
  activeTwilioCallSid,
  voicemailUrl,
  onVoicemailDrop,
  /** When true (e.g. front desk confirmation calls), hide Drop Voicemail UI entirely. */
  hideVoicemailDrop = false,
}) => {
  const { isMuted, callDuration, callStatus, isInCall, toggleMute, hangUp, sendDigits } = useTwilio();
  const [showDtmf, setShowDtmf] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [vmDropping, setVmDropping] = useState(false);
  const [vmDropped, setVmDropped] = useState(false);

  const isConnected = callStatus === "connected";
  const isRinging = callStatus === "ringing" || callStatus === "initiating";
  const isCompleted = callStatus === "completed" && !isInCall;

  // Listen for twilio-call-ended to notify parent
  useEffect(() => {
    const handler = () => {
      onCallComplete?.(callStatus, callDuration);
    };
    window.addEventListener("twilio-call-ended", handler);
    return () => window.removeEventListener("twilio-call-ended", handler);
  }, [callStatus, callDuration, onCallComplete]);

  // Auto-dismiss when call is completed — give parent time to show wrap-up
  useEffect(() => {
    if (isCompleted) {
      const timer = setTimeout(() => {
        onEndCall?.();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isCompleted]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEndCall = () => {
    if (isInCall) {
      hangUp();
    }
    onEndCall?.();
  };

  const handleVoicemailDrop = async () => {
    if (!activeTwilioCallSid || vmDropped) return;
    setVmDropping(true);
    onVoicemailDrop?.(true); // Inform parent to prevent canceling call on disconnect
    try {
      await callService.dropVoicemail(activeTwilioCallSid, voicemailUrl || "");
      setVmDropped(true);
    } catch {
      onVoicemailDrop?.(false); // Reset if failed
    } finally {
      setVmDropping(false);
    }
  };

  const statusDisplay = (() => {
    if (isCompleted) return { text: "Call Ended", color: "text-rose-500", bg: "bg-rose-50", dot: "bg-rose-400" };
    if (isConnected) return { text: "On Call", color: "text-emerald-500", bg: "bg-emerald-50", dot: "bg-emerald-500" };
    if (isRinging) return { text: "Ringing...", color: "text-blue-500", bg: "bg-blue-50", dot: "bg-blue-500" };
    return { text: callStatus || "Connecting...", color: "text-amber-500", bg: "bg-amber-50", dot: "bg-amber-500" };
  })();

  if (minimized) {
    return (
      <div className="fixed bottom-6 right-6 z-[60]">
        <button
          onClick={() => setMinimized(false)}
          className={clsx(
            "flex items-center gap-2 px-4 py-2.5 rounded-full shadow-xl border",
            isConnected ? "bg-emerald-500 text-white border-emerald-600" : "bg-blue-500 text-white border-blue-600"
          )}
        >
          <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
          <span className="text-sm font-medium">{formatDuration(callDuration)}</span>
          <span className="text-xs opacity-80">{lead?.firstName}</span>
          <ChevronUpIcon className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-[60] w-80">
      <div className="rounded-2xl bg-white border border-surface-200 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className={clsx("px-4 py-3 flex items-center justify-between", statusDisplay.bg)}>
          <div className="flex items-center gap-2">
            <div className={clsx("w-2 h-2 rounded-full animate-pulse", statusDisplay.dot)} />
            <span className={clsx("text-sm font-medium", statusDisplay.color)}>{statusDisplay.text}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-surface-500">
              <ClockIcon className="w-4 h-4" />
              <span className="text-sm font-mono">{formatDuration(callDuration)}</span>
            </div>
            <button onClick={() => setMinimized(true)} className="p-1 hover:bg-white/50 rounded">
              <ChevronDownIcon className="w-4 h-4 text-surface-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold">
              <UserCircleIcon className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-surface-900 truncate">
                {lead?.firstName} {lead?.lastName}
              </h4>
              <p className="text-xs text-surface-500 font-mono truncate">
                {lead?.contact?.phone || lead?.phone}
              </p>
              {lead?.stageName && (
                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                  {lead.stageName}
                </span>
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={toggleMute}
              disabled={isCompleted}
              className={clsx(
                "p-3 rounded-full transition-colors",
                isCompleted
                  ? "bg-surface-100 text-surface-300 cursor-not-allowed"
                  : isMuted
                    ? "bg-red-100 text-red-600"
                    : "bg-surface-50 text-surface-600 hover:bg-surface-100"
              )}
              title={isCompleted ? "Call ended" : isMuted ? "Unmute" : "Mute"}
            >
              <MicrophoneIcon className={clsx("w-5 h-5", isMuted && "line-through")} />
            </button>

            <button
              onClick={handleEndCall}
              className={clsx(
                "p-4 rounded-full shadow-lg transition-all hover:scale-105",
                isCompleted
                  ? "bg-surface-400 text-white shadow-surface-400/30"
                  : "bg-rose-500 text-white shadow-rose-500/30 hover:bg-rose-600"
              )}
              title={isCompleted ? "Dismiss" : "End Call"}
            >
              <PhoneXMarkIcon className="w-6 h-6" />
            </button>

            <button
              onClick={() => setShowDtmf(!showDtmf)}
              disabled={isCompleted}
              className={clsx(
                "p-3 rounded-full transition-colors",
                isCompleted
                  ? "bg-surface-100 text-surface-300 cursor-not-allowed"
                  : showDtmf
                    ? "bg-blue-100 text-blue-600"
                    : "bg-surface-50 text-surface-600 hover:bg-surface-100"
              )}
              title="Keypad"
            >
              <HashtagIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Voicemail Drop — hidden for front desk */}
          {!hideVoicemailDrop && isConnected && !vmDropped && (
            <button
              onClick={handleVoicemailDrop}
              disabled={vmDropping || !voicemailUrl}
              className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium hover:bg-amber-100 transition-colors disabled:opacity-50"
              title={!voicemailUrl ? "Select a voicemail from the dropdown above" : ""}
            >
              <InboxArrowDownIcon className="w-4 h-4" />
              {vmDropping ? "Dropping..." : !voicemailUrl ? "No Voicemail Selected" : "Drop Voicemail"}
            </button>
          )}
          {!hideVoicemailDrop && vmDropped && (
            <div className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">
              Voicemail dropped
            </div>
          )}

          {/* DTMF Pad */}
          {showDtmf && (
            <div className="mt-3 grid grid-cols-3 gap-1.5">
              {DTMF_KEYS.flat().map((key) => (
                <button
                  key={key}
                  onClick={() => sendDigits(key)}
                  className="p-2.5 rounded-lg bg-surface-50 hover:bg-surface-100 text-surface-900 font-semibold text-lg transition-colors"
                >
                  {key}
                </button>
              ))}
            </div>
          )}
        </div>

        {isConnected && !showDtmf && (
          <div className="px-4 py-2 bg-surface-50 border-t border-surface-100">
            <p className="text-xs text-surface-500 text-center">Remember to take notes after the call</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveCallWidget;
